import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublicPost } from '../api/socialActions';
import { thumbUrl } from '../api/posts';
import { Lock, Image as ImgIcon, Video as VideoIcon } from 'lucide-react';

/**
 * Phase 50 · Rich preview card for shared posts inside DM bubbles.
 *
 * Fed by the backend's `shared_post_id` field on MessageOut. Falls back
 * silently on 404 / access errors so the DM never breaks.
 */
const SharedPostPreview = ({ postId, mine }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await fetchPublicPost(postId);
        if (alive) setPost(p);
      } catch {
        if (alive) setErr(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [postId]);

  if (loading) {
    return (
      <div className={`mt-1 h-16 w-full animate-pulse rounded-lg ${mine ? 'bg-amber-600/20' : 'bg-neutral-700/40'}`} />
    );
  }
  if (err || !post) {
    return (
      <div className={`mt-1 rounded-lg px-2 py-1 text-[11px] ${mine ? 'bg-amber-600/20 text-amber-100' : 'bg-neutral-700/40 text-muted-foreground'}`}>
        Post non disponibile
      </div>
    );
  }

  const Icon = post.media_type === 'video' ? VideoIcon : ImgIcon;
  return (
    <Link
      to={`/post/${post.id}`}
      className={`mt-1 flex items-center gap-2 rounded-lg border px-2 py-1.5 no-underline transition-colors ${
        mine
          ? 'border-amber-600/40 bg-amber-600/10 hover:bg-amber-600/20'
          : 'border-neutral-700/60 bg-neutral-800/50 hover:bg-neutral-800'
      }`}
      data-testid={`shared-post-preview-${post.id}`}
    >
      {post.has_access && post.media_url ? (
        post.media_type === 'video' ? (
          post.poster_url ? (
            <img
              src={thumbUrl(post.poster_url, 160)}
              alt="video"
              className="h-10 w-10 flex-shrink-0 rounded-md object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-black">
              <VideoIcon className="h-4 w-4 text-white" />
            </div>
          )
        ) : (
          <img
            src={thumbUrl(post.media_url, 160)}
            alt="post"
            className="h-10 w-10 flex-shrink-0 rounded-md object-cover"
            loading="lazy"
          />
        )
      ) : (
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ${mine ? 'bg-amber-700/40' : 'bg-neutral-700'}`}>
          {post.is_paid ? <Lock className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className={`text-[10px] font-semibold uppercase tracking-wide ${mine ? 'text-amber-800/80' : 'text-muted-foreground'}`}>
          Post condiviso
        </p>
        <p className={`truncate text-xs ${mine ? 'text-neutral-900' : 'text-foreground'}`}>
          @{post.author?.username || 'nymia'} · {post.short_id}
        </p>
      </div>
    </Link>
  );
};

export default SharedPostPreview;
